import React, { useState, useEffect, useRef } from "react";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import Modal from "./Modal";
import { saveDraft, loadDraft, clearDraft, scopedDraftKey } from "@/lib/formDraftStorage";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { EntryType } from "@/types";
import ExpenseEntryForm from "./entry-modal/forms/ExpenseEntryForm";
import EntryTypeSelector from "./entry-modal/EntryTypeSelector";
import { getEntryTypeLabel } from "./entry-modal/utils";
import { Button } from "./ui/button";
import CheckForm from "./entry-modal/forms/CheckForm";
import LaborCostForm from "./entry-modal/forms/LaborCostForm";
import DeliveryForm from "./entry-modal/forms/DeliveryForm";
import SitePhotoForm from "./entry-modal/forms/SitePhotoForm";
import { Progress } from "@/components/ui/progress";
import PersonModal from "./PersonModal";
import { peopleAPI } from "@/api/people";
import { useAuth } from "@/contexts/AuthContext";
import { getStorageBackendName } from "@/lib/storage-adapter";
import { useProjectRole } from "@/hooks/useProjectRole";
import { ScreenHeader } from "@/components/redesign";

// Draft keys for the entry flow. The modal owns the shared fields (used by the
// labor/check/delivery/site-photo forms and the selected type); ExpenseEntryForm
// owns its own richer react-hook-form draft under EXPENSE_FORM_DRAFT.
const ENTRY_MODAL_DRAFT = "entry-modal";
const EXPENSE_FORM_DRAFT = "expense-entry";
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface EntryTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (entryData: any) => void;
  initialData?: {
    type: string;
    date: Date;
    dueDate?: Date;
    projectId: string;
    personId?: string;
    description: string;
    amount?: number;
    imageUrl?: string;
    currency?: string;
    original_currency?: string;
    original_amount?: number;
    hasInvoice?: boolean;
  };
  // Add props for progress indication from parent
  uploadProgress?: number;
  uploadStatus?: 'idle' | 'uploading' | 'success' | 'error';
  isUploading?: boolean;
}

const EntryTypeModal: React.FC<EntryTypeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  // Add props with defaults
  uploadProgress: parentUploadProgress,
  uploadStatus: parentUploadStatus,
  isUploading: parentIsUploading
}) => {
  
  
  // Wrap onClose with debugging
  const debugOnClose = () => {
    
    onClose();
  };
  
  // State
  const [selectedType, setSelectedType] = useState<EntryType | null>(initialData?.type as EntryType || null);
  const [date, setDate] = useState<Date>(initialData?.date || new Date());
  const [dueDate, setDueDate] = useState<Date>(initialData?.dueDate || new Date());
  const [projectId, setProjectId] = useState(initialData?.projectId || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [amount, setAmount] = useState(initialData?.amount?.toString() || "");
  const [personId, setPersonId] = useState(initialData?.personId || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(initialData?.imageUrl || null);
  const [imageUrl, setImageUrl] = useState<string | undefined>(initialData?.imageUrl);
  const [currency, setCurrency] = useState<string>(initialData?.currency || initialData?.original_currency || 'TRY');
  const [hasInvoice, setHasInvoice] = useState<boolean>(initialData?.hasInvoice || false);
  const [originalCurrency, setOriginalCurrency] = useState<string>(initialData?.original_currency || 'TRY');
  const [originalAmount, setOriginalAmount] = useState<number | undefined>(initialData?.original_amount);
  
  // Upload progress state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  
  // Get role-based permissions
  const permissions = useProjectRole(projectId);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // Person modal state
  const [personModalOpen, setPersonModalOpen] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { user } = useAuth();

  // Debug: Track navigation changes
  useEffect(() => {
    
  }, []);

  // Debug: Track component lifecycle
  useEffect(() => {
    
    return () => {
      
    };
  }, []);

  // Update local state from parent props if available
  useEffect(() => {
    if (parentUploadProgress !== undefined) {
      setUploadProgress(parentUploadProgress);
    }
    if (parentUploadStatus !== undefined) {
      setUploadStatus(parentUploadStatus);
    }
    if (parentIsUploading !== undefined) {
      setIsUploading(parentIsUploading);
    }
  }, [parentUploadProgress, parentUploadStatus, parentIsUploading]);

  // Keep form preselection in sync with quick-action query params.
  useEffect(() => {
    if (!initialData) return;

    if (initialData.type) {
      setSelectedType(initialData.type as EntryType);
    }

    if (initialData.projectId) {
      setProjectId(initialData.projectId);
    }
  }, [initialData?.type, initialData?.projectId]);

  // --- Draft persistence: keep in-progress entry data across background/app kill ---
  const draftInitRef = useRef(false);
  const draftSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Restore a saved draft once on mount — but only for a fresh entry. A
  // deep-link preset (initialData) should always win, so we skip restore then.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!initialData) {
        const draft = await loadDraft<any>(scopedDraftKey(user?.id, ENTRY_MODAL_DRAFT), {
          maxAgeMs: DRAFT_MAX_AGE_MS,
        });
        if (!cancelled && draft && typeof draft === "object") {
          if (draft.selectedType) setSelectedType(draft.selectedType);
          if (draft.date instanceof Date) setDate(draft.date);
          if (draft.dueDate instanceof Date) setDueDate(draft.dueDate);
          if (typeof draft.projectId === "string") setProjectId(draft.projectId);
          if (typeof draft.description === "string") setDescription(draft.description);
          if (typeof draft.amount === "string") setAmount(draft.amount);
          if (typeof draft.personId === "string") setPersonId(draft.personId);
          if (typeof draft.currency === "string") setCurrency(draft.currency);
          if (typeof draft.hasInvoice === "boolean") setHasInvoice(draft.hasInvoice);
          if (typeof draft.originalCurrency === "string") setOriginalCurrency(draft.originalCurrency);
          if (typeof draft.originalAmount === "number") setOriginalAmount(draft.originalAmount);
        }
      }
      // Enable autosave only after the restore attempt, so the first render's
      // empty defaults can never overwrite a previously saved draft.
      if (!cancelled) draftInitRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Debounced autosave of the shared entry state (or clear when pristine).
  useEffect(() => {
    if (!draftInitRef.current) return;
    const key = scopedDraftKey(user?.id, ENTRY_MODAL_DRAFT);
    const hasInput =
      selectedType !== null ||
      description.trim() !== "" ||
      amount.trim() !== "" ||
      projectId !== "" ||
      personId !== "";
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => {
      if (hasInput) {
        void saveDraft(key, {
          selectedType,
          date,
          dueDate,
          projectId,
          description,
          amount,
          personId,
          currency,
          hasInvoice,
          originalCurrency,
          originalAmount,
        });
      } else {
        void clearDraft(key);
      }
    }, 600);
    return () => {
      if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedType,
    date,
    dueDate,
    projectId,
    description,
    amount,
    personId,
    currency,
    hasInvoice,
    originalCurrency,
    originalAmount,
    user?.id,
  ]);

  // Add event listener for reset-entry-selection event
  useEffect(() => {
    const handleResetEntrySelection = () => {
      
      resetForm();
    };

    window.addEventListener('reset-entry-selection', handleResetEntrySelection);
    
    return () => {
      window.removeEventListener('reset-entry-selection', handleResetEntrySelection);
    };
  }, []);

  // Handle person change from any form
  const handlePersonChange = (value: string) => {
    
    if (value === "new") {
      // Open person modal while preserving form state
      setPersonModalOpen(true);
    } else {
      setPersonId(value);
    }
  };

  // Handle adding a new person
  const handleAddPerson = async (data: any) => {
    try {
      // Create the person in the database
      
      const newPerson = await peopleAPI.createPerson({
        name: data.name,
        category: data.category,
        specialty: data.specialty,
        phone: data.phone || '',
        email: data.email || '',
        created_by: user.id
      });
      
      
      
      // Set the new person ID in the form
      setPersonId(newPerson.id);
      
      // Trigger a custom event to refresh people data in components
      const event = new CustomEvent('people-data-refresh', { detail: { action: 'add', person: newPerson } });
      window.dispatchEvent(event);
      
      // Show success message
      toast({
        title: t("entry_modal.person_added"),
        description: t("entry_modal.person_added_success", { name: data.name }),
      });
    } catch (error: any) {
      
      
      // Show error message
      toast({
        title: t("entry_modal.person_add_failed"),
        description: error.message || t("entry_modal.person_add_error"),
        variant: "destructive",
      });
    } finally {
      // Close the modal and return to the form
      setPersonModalOpen(false);
    }
  };

  // Handle currency change
  const handleCurrencyChange = (value: string) => {
    setCurrency(value);
  };

  // Handle entry type selection
  const handleEntryTypeSelect = (type: EntryType) => {
    
    
    
    if (type === "sale") {
      
      // Navigate to SalePage for sale entries
      navigate("/salepage");
      
      
      debugOnClose();
      
      
      // Debug: Check if navigation happened after a delay
      setTimeout(() => {
        
      }, 100);
      
      setTimeout(() => {
        
      }, 500);
    } else {
      
      setSelectedType(type);
    }
  };

  // Form submission handler
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get the storage backend name
    const storageBackend = getStorageBackendName();
    
    if (!selectedType) {
      
      return;
    }
    
    
    
    
    // Validate form data
    if (!projectId) {
      
      toast({
        title: t("common.error"),
        description: t("entry_modal.validations.select_project"),
        variant: "destructive",
      });
      return;
    }
    
    if (selectedType === "laborCost" && !personId) {
      
      toast({
        title: t("common.error"),
        description: t("entry_modal.validations.select_person"),
        variant: "destructive",
      });
      return;
    }
    
    if ((selectedType === "laborCost" || selectedType === "expense" || 
         selectedType === "givenCheck" || selectedType === "receivedCheck") && 
        (!amount || isNaN(parseFloat(amount)))) {
      
      toast({
        title: t("common.error"),
        description: t("entry_modal.validations.enter_valid_amount"),
        variant: "destructive",
      });
      return;
    }
    
    const formData = {
      type: selectedType,
      date,
      dueDate: selectedType === "givenCheck" || selectedType === "receivedCheck" ? dueDate : undefined,
      projectId,
      personId: personId || undefined,
      description,
      amount: selectedType !== "sitePhoto" && selectedType !== "delivery" ? parseFloat(amount) : undefined,
      currency: currency,
      imageFile,
      imageUrl: imageUrl,
    };
    
    
    
    // If we have an image file, only show toast and let onSubmit handle navigation
    if (imageFile) {
      // Show upload in progress
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus('uploading');
      setUploadError(null);
      
      toast({
        title: t("entry_modal.saving_entry", { type: getEntryTypeLabel(selectedType) }),
        description: t("entry_modal.uploading_image", { storage: storageBackend }),
      });
      resetForm();
      try {
        onSubmit(formData);
        
      } catch (error) {
        
        toast({
          title: t("entry_modal.submission_error"),
          description: t("entry_modal.form_submission_error", { storage: storageBackend }),
          variant: "destructive",
        });
      }
    } else {
      // For submissions without images, we can handle navigation here
      try {
        onSubmit(formData);
        
        
        // Show success toast
        toast({
          title: t("entry_modal.entry_saved", { type: getEntryTypeLabel(selectedType) }),
          description: t("entry_modal.redirecting_home"),
        });
        
        // Reset form and navigate
        resetForm();
        setTimeout(() => {
          onClose();
        }, 2500);
      } catch (error) {
        
        toast({
          title: t("entry_modal.submission_error"),
          description: t("entry_modal.form_submission_error", { storage: storageBackend }),
          variant: "destructive",
        });
      }
    }
  };

  // Handle expense form submit
  const handleExpenseSubmit = (data: any) => {
    // Get the storage backend name
    const storageBackend = getStorageBackendName();
    
    
    
    
    // Try-catch to catch any synchronous errors during submission
    try {
      // Ensure we have the correct fields from the expense form
      const formattedData = {
        ...data,
        type: "expense",
        projectId: data.projectId,
        description: data.description,
        amount: data.amount,
        original_amount: parseFloat(data.amount),
        original_currency: data.original_currency,
        currency: data.original_currency, // For backward compatibility
        is_official: data.hasInvoice,
        personId: data.personId,
        date: data.date,
        imageFile: data.imageFile
      };
      
      
      
      // Make sure to extract the imageFile if it exists
      if (data.imageFile) {
        
        
        // Show upload in progress indicator
        setIsUploading(true);
        setUploadProgress(0);
        setUploadStatus('uploading');
        setUploadError(null);
        
        // Let the modal stay open while the parent handles the upload
        toast({
          title: t("entry_modal.expense_saving"),
          description: t("entry_modal.uploading_image", { storage: storageBackend }),
        });
        
        // We'll keep the modal open, parent component will handle the upload
        resetForm();
        
        // Submit the form data with imageFile to the parent component
        onSubmit({
          ...formattedData,
          // Add a callback that will be triggered when the upload is complete
          onUploadComplete: () => {
            setUploadStatus('success');
            setUploadProgress(100);
            
            // Show success message
            toast({
              title: t("entry_modal.expense_saved"),
              description: t("entry_modal.expense_image_uploaded", { storage: storageBackend }),
            });
            
            // Give time to see the success message
            setTimeout(() => {
              onClose();
            }, 2500);
          }
        });
      } else {
        toast({
          title: t("entry_modal.expense_saved"),
          description: t("entry_modal.redirecting_home"),
        });
        
        // Only set timeout for immediate close if there's no image
        resetForm();
        onSubmit(formattedData);
        
        setTimeout(() => {
          onClose();
        }, 2500);
      }
      
      
    } catch (error) {
      
      toast({
        title: t("entry_modal.submission_error"),
        description: t("entry_modal.expense_save_error", { storage: storageBackend }),
        variant: "destructive",
      });
    }
  };

  // Handle image upload
  const handleImageUpload = (file: File) => {
    // Validate file type and size before accepting
    const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast({
        title: t("entry_modal.file_validation.invalid_format"),
        description: t("entry_modal.file_validation.invalid_format_desc"),
        variant: "destructive",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: t("entry_modal.file_validation.file_too_large"),
        description: t("entry_modal.file_validation.file_too_large_desc"),
        variant: "destructive",
      });
      return;
    }

    setImageFile(file);
    // When a new file is uploaded, we clear the existing image URL
    setImageUrl(undefined);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Reset form state
  const resetForm = () => {
    setSelectedType(null);
    setDate(new Date());
    setDueDate(new Date());
    setProjectId("");
    setPersonId("");
    setDescription("");
    setAmount("");
    setImageFile(null);
    setImagePreview(null);
    setImageUrl(undefined);
    setCurrency('TRY');
    setHasInvoice(false);
    setOriginalCurrency('TRY');
    setOriginalAmount(undefined);

    // Entry submitted / discarded — drop both entry drafts immediately so a
    // close that unmounts the modal can't leave a stale draft behind.
    void clearDraft(scopedDraftKey(user?.id, ENTRY_MODAL_DRAFT));
    void clearDraft(scopedDraftKey(user?.id, EXPENSE_FORM_DRAFT));
  };

  const handleClose = () => {
    
    
    
    
    // If upload is in progress, ask for confirmation
    if (isUploading && uploadStatus === 'uploading') {
      const confirmed = window.confirm(t("entry_modal.upload_confirmation.title"));
      if (!confirmed) return;
    }
    
    resetForm();
    setUploadStatus('idle');
    setUploadProgress(0);
    setIsUploading(false);
    onClose();
    
    // If we're on the NewEntryPage, navigate back to home
    if (window.location.pathname.includes('/new-entry')) {
      
      navigate('/');
    } else {
      
    }
  };

  // Should show currency selector
  const shouldShowCurrencySelector = () => {
    return selectedType === "laborCost" || 
           selectedType === "givenCheck" || 
           selectedType === "receivedCheck";
  };

  // Get modal title based on selected type
  const getModalTitle = () => {
    if (!selectedType) return t("entry_modal.title");
    
    switch(selectedType) {
      case "expense":
        return t("entry_modal.labels.expense_add");
      case "givenCheck":
        return t("entry_modal.labels.given_check_add");
      case "receivedCheck":
        return t("entry_modal.labels.received_check_add");
      case "laborCost":
        return t("entry_modal.labels.labor_cost_add");
      case "delivery":
        return t("entry_modal.labels.delivery_add");
      case "sitePhoto":
        return t("entry_modal.labels.site_photo_add");
      case "sale":
        return t("entry_modal.labels.sale_add");
      default:
        return t("entry_modal.title");
    }
  };

  // Render upload status
  const renderUploadStatus = () => {
    if (!isUploading) return null;

    return (
      <div className="mx-4 my-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="space-y-3">
          <h4 className="text-center text-[13px] font-semibold text-gray-900">{t("entry_modal.upload_status.title")}</h4>

          {uploadStatus === 'uploading' && (
            <>
              <Progress value={uploadProgress} className="w-full h-1.5" />
              <div className="flex items-center justify-center text-[11px] text-gray-500">
                <Loader2 className="h-3 w-3 mr-2 animate-spin text-brand-600" />
                <span>{t("entry_modal.upload_status.uploading", { progress: Math.round(uploadProgress) })}</span>
              </div>
              <p className="text-[10px] text-center text-gray-400">{t("entry_modal.upload_status.wait_message")}</p>
            </>
          )}

          {uploadStatus === 'success' && (
            <div className="flex items-center justify-center text-success-600 py-1">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              <span className="text-[13px] font-medium">{t("entry_modal.upload_status.success")}</span>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="flex items-center justify-center text-error-600 py-1">
              <AlertCircle className="h-4 w-4 mr-2" />
              <span className="text-[13px] font-medium">{uploadError || t("entry_modal.upload_status.error")}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render different form content based on selected type
  const renderFormContent = () => {
    if (!selectedType) {
      return (
        <EntryTypeSelector
          selectedType={selectedType}
          onSelectType={handleEntryTypeSelect}
        />
      );
    }

    switch (selectedType) {
      case "expense":
        return <ExpenseEntryForm 
          onSubmit={handleExpenseSubmit} 
          initialData={{
            date,
            projectId,
            description,
            amount: amount ? parseFloat(amount) : undefined,
            hasInvoice,
            personId,
            imageUrl,
            original_currency: originalCurrency,
            currency
          }}
        />;
      case "laborCost":
        return (
          <LaborCostForm
            date={date}
            projectId={projectId}
            personId={personId}
            description={description}
            amount={amount}
            imageFile={imageFile}
            imagePreview={imagePreview}
            onDateChange={setDate}
            onProjectChange={setProjectId}
            onPersonChange={handlePersonChange}
            onDescriptionChange={setDescription}
            onAmountChange={setAmount}
            onImageChange={handleImageUpload}
            onSubmit={handleFormSubmit}
            currency={currency}
            onCurrencyChange={handleCurrencyChange}
          />
        );
      case "givenCheck":
      case "receivedCheck":
        return (
          <CheckForm
            date={date}
            dueDate={dueDate}
            projectId={projectId}
            personId={personId}
            description={description}
            amount={amount}
            isGivenCheck={selectedType === "givenCheck"}
            imageFile={imageFile}
            imagePreview={imagePreview}
            onDateChange={setDate}
            onDueDateChange={setDueDate}
            onProjectChange={setProjectId}
            onPersonChange={handlePersonChange}
            onDescriptionChange={setDescription}
            onAmountChange={setAmount}
            onImageChange={handleImageUpload}
            onSubmit={handleFormSubmit}
            currency={currency}
            onCurrencyChange={handleCurrencyChange}
          />
        );
      case "delivery":
        return (
          <DeliveryForm
            date={date}
            projectId={projectId}
            description={description}
            imageFile={imageFile}
            imagePreview={imagePreview}
            onDateChange={setDate}
            onProjectChange={setProjectId}
            onDescriptionChange={setDescription}
            onImageChange={handleImageUpload}
            onSubmit={handleFormSubmit}
          />
        );
      case "sitePhoto":
        return (
          <SitePhotoForm
            date={date}
            projectId={projectId}
            description={description}
            imageFile={imageFile}
            imagePreview={imagePreview}
            onDateChange={setDate}
            onProjectChange={setProjectId}
            onDescriptionChange={setDescription}
            onImageChange={handleImageUpload}
            onSubmit={handleFormSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} className="max-w-md mx-auto">
        {/* Negative margin counteracts Modal's p-4 wrapper so header bleeds edge-to-edge */}
        <div className="-mx-4 -mt-4">
          {/* Navy screen header */}
          <ScreenHeader
            title={getModalTitle()}
            backLabel={t("common.back", { defaultValue: "Geri" })}
            onBack={handleClose}
            className="rounded-t-xl"
          />
        </div>

        {/* Upload progress indicator */}
        <div className="-mx-4">
          {renderUploadStatus()}
        </div>

        {/* White form surface */}
        <div className="-mx-4 bg-gray-25 min-h-[300px]">
          {!selectedType && (
            <div className="px-4 pt-4 pb-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-500 mb-3">
                {t("entry_modal.select_type")}
              </p>
            </div>
          )}
          {renderFormContent()}
        </div>
      </Modal>

      {/* Person Modal for adding new people */}
      <PersonModal
        isOpen={personModalOpen}
        onClose={() => setPersonModalOpen(false)}
        onSubmit={handleAddPerson}
        isEditing={false}
      />
    </>
  );
};

export default EntryTypeModal;
